import clsx from 'clsx';
import Link from 'next/link';
import { memo, useCallback, useMemo } from 'react';
import { ShieldAlert, ShieldCheck } from '@music-vine/cadence/icons';
import { useAccountModals } from '@/components/Modals/Account';
import { AccountModalNames, ModalConversionPoints } from '@/components/Modals/shared-types';
import { UBSafelist } from '@/components/UBSafelist';
import { useWhitelistedChannels } from '@/components/UBSafelist/hooks/useWhitelistedChannels';
import { Button } from '@music-vine/cadence/ui';
import { Text } from '@music-vine/cadence/ui';
import { useHasPermission, useHasRole, useSubscriptionType } from '@/hooks/auth';
import { AccountType } from '@/hooks/billing/useAccountType';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { useSubscribeModal } from '@/hooks/billing/useSubscribeModal';
import { usePopover } from '@/components/Popover/context';
import { cn } from '@/lib/utils';
import { getMaxSafelistedChannels } from './getMaxSafelistedChannels';
import { SafelistPopoverFooter } from './SafelistPopoverFooter';
import { useSettings } from '@/hooks/state/useSettings';
import { useStage } from '@/components/DialogStage';
const MOBILE_BOTTOM_CLEARANCE = 'max-xs:pb-12';
const SafelistContainer = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) =>
// twMerge so callers can override the base spacing without `!`.
<div className={cn('mx-auto flex flex-col items-start gap-2', className)} data-sentry-component="SafelistContainer" data-sentry-source-file="SafelistContent.tsx">
    {children}
  </div>;
const SafelistCallout = ({
  body,
  buttonLabel,
  className,
  onClick,
  title
}: {
  body: string;
  buttonLabel: string;
  className?: string;
  onClick: () => void;
  title: string;
}) => <SafelistContainer className={clsx(className, 'px-5 pb-5', MOBILE_BOTTOM_CLEARANCE)} data-sentry-element="SafelistContainer" data-sentry-component="SafelistCallout" data-sentry-source-file="SafelistContent.tsx">
    <h3 className="flex flex-nowrap items-center gap-2 font-semibold text-gray-950 text-lg tracking-tight 2xl:text-xl dark:text-white">
      {title}
    </h3>
    <p className="mb-1 text-pretty text-gray-700 text-sm dark:text-gray-200">
      {body}
    </p>
    <Button className="w-full md:w-fit" fontSize={{
    default: 'sm'
  }} onClick={onClick} size={{
    default: 'sm',
    '2xl': 'default'
  }} variant="brand" data-sentry-element="Button" data-sentry-source-file="SafelistContent.tsx">
      {buttonLabel}
    </Button>
  </SafelistContainer>;
interface SafelistProps {
  className?: string;
  onUpgrade: () => void;
}
const SafelistContent = memo(({
  className,
  onUpgrade
}: SafelistProps) => {
  const {
    openModalWithConversionPoint
  } = useAccountModals();
  const {
    openSubscribeModalWithConversion
  } = useSubscribeModal();
  const {
    setIsOpen
  } = usePopover();

  // Dismiss the popover before the upgrade UI opens: the chooser is rendered
  // as a sibling of the popover, so it survives this close.
  const handleUpgrade = useCallback(() => {
    setIsOpen(false);
    onUpgrade();
  }, [onUpgrade, setIsOpen]);
  const isFree = useHasRole('Free');
  const subscriptionType = useSubscriptionType();
  const {
    settings
  } = useSettings();
  const {
    open
  } = useStage();
  const isPaused = settings?.subscriptionData?.isPaused;
  const {
    data: whitelistedChannels
  } = useWhitelistedChannels();
  const {
    overduePayment
  } = useOverduePayment();
  const {
    id: overduePaymentId,
    overdueDate
  } = overduePayment ?? {};
  const hasWhitelistedChannels = whitelistedChannels !== undefined && whitelistedChannels.length > 0;
  const hasUnprotectedChannels = hasWhitelistedChannels && isFree;
  const oldAccountType = hasUnprotectedChannels && whitelistedChannels.length <= 3 ? AccountType.Premium : AccountType.Business;
  const hasSafelistPermission = useHasPermission('safelist_yt');
  const maxSafelistedChannels = getMaxSafelistedChannels({
    subscriptionType
  });
  const shieldIconInTitle = useMemo(() => {
    if (overduePaymentId) {
      return <ShieldAlert className="text-red-600 dark:text-red-400" />;
    }
    if (!hasUnprotectedChannels) {
      return <ShieldCheck className="text-green-600 dark:text-green-400" />;
    }
    return null;
  }, [hasUnprotectedChannels, overduePaymentId]);
  const channelText = maxSafelistedChannels === 1 ? 'channel' : 'channels';
  if (isPaused) {
    return <SafelistCallout body="You can't change channels while paused, but your safelist stays active and your content stays protected." buttonLabel="Resume Subscription" className={className} onClick={() => open('cancellation.resume')} title="Safelist locked while paused" />;
  }
  if (!(hasSafelistPermission || hasUnprotectedChannels)) {
    return <SafelistCallout body="Subscribe to safelist your channels and automatically avoid copyright claims on YouTube." buttonLabel="Subscribe now" className={className} onClick={() => openSubscribeModalWithConversion(ModalConversionPoints.AccountSidebar)} title="Safelist your channels" />;
  }
  return <SafelistContainer className={clsx(className, 'gap-0', MOBILE_BOTTOM_CLEARANCE)}>
      <div className="flex w-full flex-col gap-3 px-6 pb-5 max-md:px-5">
        <h3 className="flex flex-nowrap items-center gap-1 max-md:justify-center">
          {shieldIconInTitle}
          {hasUnprotectedChannels ? <Text className="font-heading font-semibold text-gray-950 text-lg tracking-tight 2xl:text-xl dark:text-white">
              Your channels are no longer safelisted
            </Text> :
        // NOTE: Added whitespace-nowrap to prevent the text from wrapping when channelText is plural.
        // Otherwise, I would have had to modify the width of the core popover container with a custom value like w-[18.125rem]
        <Text className="whitespace-nowrap font-heading font-semibold text-gray-950 text-lg tracking-tight 2xl:text-xl dark:text-white">
              Your safelisted {channelText}
            </Text>}
        </h3>
        {!hasUnprotectedChannels && <p className="text-gray-700 text-sm max-md:text-center dark:text-gray-200">
            {maxSafelistedChannels === 1 ? 'Safelist your channel to avoid claims while using Uppbeat music.' : `Safelist up to ${maxSafelistedChannels} YouTube ${channelText} to avoid claims while using Uppbeat music.`}
          </p>}
        <div className="xs:max-h-[300px] min-w-full overflow-y-auto">
          <UBSafelist compact fullWidth hideTrashIcon relativePosition />
        </div>

        {!!hasUnprotectedChannels && <div className="flex flex-col items-start gap-2">
            <p className="text-sm">
              {`Streamline your creative process by unlocking the ability to
                safelist up to ${maxSafelistedChannels} YouTube channels with Uppbeat ${oldAccountType === AccountType.Premium ? 'Premium' : 'Business'}.
                Safelisting saves you from needing to credit Uppbeat each time you
                use one of our tracks.`}
            </p>
            <Button onClick={() => openModalWithConversionPoint(AccountModalNames.SignUp, ModalConversionPoints.SignUp)} variant="brand">
              Upgrade now
            </Button>
          </div>}
        <Link className="text-pink-500 text-xs underline hover:underline max-md:text-sm 2xl:text-sm dark:text-white" href="/my/account/safelist">
          Manage {channelText}
        </Link>
      </div>
      <SafelistPopoverFooter maxSafelistedChannels={maxSafelistedChannels} onUpgrade={handleUpgrade} overdueDate={overdueDate} overduePaymentId={overduePaymentId} whitelistedChannels={whitelistedChannels} />
    </SafelistContainer>;
});
SafelistContent.displayName = 'SafelistContent';
export default SafelistContent;